import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { X, Plus } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useToast } from '@/hooks/use-toast'
import { apiClient } from '@/lib/api'
import { formatDate } from '@/lib/utils'
import type { Contact, Lead } from '@/types'

interface Props {
  /** A Contact or Lead — both carry the same Mailchimp fields (v1.1). */
  contact: Contact | Lead
  /** Marketing role users and admins may edit tags; others are read-only. */
  canEditTags: boolean
  /** Which object this is — routes the tag save + cache invalidation. */
  entityType?: 'contact' | 'lead'
}

// System-managed deliverability state → badge styling. All read-only.
const STATE_STYLES: Record<string, string> = {
  Subscribed: 'bg-[#AFDBB1] text-[#072744]',
  Unsubscribed: 'bg-[#F5F5F5] text-[#072744]',
  'Hard Bounced': 'bg-[#941052] text-white',
  'Soft Bounced': 'bg-[#FFC857] text-[#072744]',
  'Spam Complaint': 'bg-[#941052] text-white',
}

export function MailchimpContactSection({ contact, canEditTags, entityType = 'contact' }: Props) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [tags, setTags] = useState<string[]>(contact.mailchimp_tags__c ?? [])
  const [newTag, setNewTag] = useState('')

  // Keep local tags in sync when the record refetches.
  useEffect(() => {
    setTags(contact.mailchimp_tags__c ?? [])
  }, [contact.mailchimp_tags__c])

  const saveMutation = useMutation({
    mutationFn: (next: string[]) =>
      entityType === 'lead'
        ? apiClient.updateLeadMailchimpTags(contact.id, next)
        : apiClient.updateContactMailchimpTags(contact.id, next),
    onSuccess: (saved) => {
      setTags(saved)
      queryClient.invalidateQueries({
        queryKey: entityType === 'lead' ? ['leads', contact.id] : ['contacts', contact.id],
      })
      toast({ title: 'Mailchimp tags updated' })
    },
    onError: (err: any) => {
      setTags(contact.mailchimp_tags__c ?? [])
      toast({
        title: 'Could not update tags',
        description: err?.response?.data?.error ?? 'Please try again.',
        variant: 'destructive',
      })
    },
  })

  const addTag = () => {
    const t = newTag.trim()
    if (!t || tags.includes(t)) {
      setNewTag('')
      return
    }
    const next = [...tags, t]
    setNewTag('')
    saveMutation.mutate(next)
  }

  const removeTag = (t: string) => {
    saveMutation.mutate(tags.filter((x) => x !== t))
  }

  const state = contact.mailchimp_subscription_state__c
  const softBounces = contact.mailchimp_soft_bounce_count__c ?? 0

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Mailchimp</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label className="text-xs font-medium text-muted-foreground">Subscription State</Label>
            <div className="mt-1">
              {state ? (
                <Badge
                  className={`${STATE_STYLES[state] ?? 'bg-[#E7F0F6] text-[#072744]'} border-0`}
                >
                  {state}
                </Badge>
              ) : (
                <span className="text-sm text-muted-foreground">Not synced</span>
              )}
            </div>
          </div>
          <div>
            <Label className="text-xs font-medium text-muted-foreground">Soft Bounces</Label>
            <p className="mt-1 text-sm">{softBounces}</p>
          </div>
          <div>
            <Label className="text-xs font-medium text-muted-foreground">Marketing Consent</Label>
            <p className="mt-1 text-sm">
              {contact.email_marketing_consent__c
                ? `Yes${
                    contact.email_marketing_consent_date__c
                      ? ` — ${formatDate(contact.email_marketing_consent_date__c)}`
                      : ''
                  }`
                : 'No (transactional only)'}
            </p>
          </div>
          {contact.mailchimp_unsubscribed_date__c && (
            <div>
              <Label className="text-xs font-medium text-muted-foreground">Unsubscribed</Label>
              <p className="mt-1 text-sm">{formatDate(contact.mailchimp_unsubscribed_date__c)}</p>
            </div>
          )}
        </div>

        <div>
          <Label className="text-xs font-medium text-muted-foreground">Mailchimp Tags</Label>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            {tags.length === 0 && <span className="text-sm text-muted-foreground">No tags</span>}
            {tags.map((t) => (
              <Badge key={t} variant="secondary" className="flex items-center gap-1">
                {t}
                {canEditTags && (
                  <button
                    type="button"
                    onClick={() => removeTag(t)}
                    disabled={saveMutation.isPending}
                    aria-label={`Remove tag ${t}`}
                    className="ml-0.5 rounded-sm hover:text-destructive focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    <X className="h-3 w-3" aria-hidden="true" />
                  </button>
                )}
              </Badge>
            ))}
          </div>

          {canEditTags && (
            <div className="mt-2 flex items-center gap-2">
              <Input
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    addTag()
                  }
                }}
                placeholder="Add a Mailchimp tag"
                aria-label="Add a Mailchimp tag"
                className="h-8 max-w-xs"
                disabled={saveMutation.isPending}
              />
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={addTag}
                disabled={saveMutation.isPending || !newTag.trim()}
              >
                <Plus className="mr-1 h-4 w-4" aria-hidden="true" />
                Add
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
